import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './BattleOverview.css';
import { useTranslation } from 'react-i18next';
import { needsTokenRefresh, refreshToken, isTokenValid } from './auth';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { getWebSocketAuthMessage, API_URL, WS_URL } from './config/apiConfig';
import CustomDropdown from './CustomDropdown';
import { updateDiscordPresence, clearDiscordPresence } from './hooks/useDiscordPresence';
import { useContainerScale } from './hooks/useViewportScale';
import { useNotifications } from './notifications';
import { TutorialMessageBox, TutorialReplayButton, useMenuTutorialReplay } from './tutorial/index';
import { normalizeStrategyValidationResponse } from './utils/normalizeStrategyValidationResponse';
import { buildExploreUnknownDevMockWinAfterBattleNavigateState } from './utils/exploreUnknownDevMockWinRewards';
import { BATTLE_OVERVIEW_MODE_STICKER_KEYS } from './battleOverview/modeStickerKeys';
import { markBattleOverviewModeEntered } from './battleOverview/markBattleOverviewModeEntered';

// Voice keys for first Battle Overview dialog: intro (3) then introGold (4) in the same dialog
const BATTLE_OVERVIEW_INTRO_VOICE_KEYS = ['battleOverview.tutorial.introText', 'battleOverview.tutorial.introTextGold'];
import AchievementUnlockedToast from './notifications/AchievementUnlockedToast';
import { usePendingAchievementToasts } from './hooks/usePendingAchievementToasts';
import { useTutorialStatus } from './hooks/useTutorialStatus';

function GameModeSticker({ labelKey, variant = 'default', visible = true }) {
  const { t } = useTranslation();
  if (!visible) return null;
  return (
    <span className={`game-mode-sticker game-mode-sticker--${variant}`}>
      {t(labelKey)}
    </span>
  );
}

// Dev: list of achievement keys and labels for simulate-achievement panel (must match titles.achievement_key).
// Emote-based titles (The Diplomat, The Gracious, etc.) are excluded so they can only be earned by actual emote usage in battles.
const DEV_ACHIEVEMENT_KEYS = [
  { key: 'secret_general_purchased', label: 'The Golden' },
  { key: 'dor_faction_Remnants', label: "Humanity's Last Hope" },
  { key: 'dor_faction_Exolot', label: "Exolot's Vengeance" },
  { key: 'dor_faction_League', label: 'The Darkest Shadow' },
  { key: 'dor_faction_Ra', label: "Ra's Favorite" },
  { key: 'dor_faction_Sentients', label: 'The Sentient' },
  { key: 'dor_faction_Asrah', label: 'The Defector' },
  { key: 'dor_all', label: 'The Absolute' },
  { key: 'dispatch_10_wins', label: 'The Sovereign' },
  { key: 'gamble_count', label: 'The Gambler' },
  { key: 'lemonades_madagascar', label: 'The Thirsty' },
  { key: 'raid_general_count', label: 'The Thief' },
  { key: 'redeem_triggered_count', label: 'The Ruthless' },
  { key: 'general_healed_total', label: 'The Healer' },
  { key: 'win_no_damage', label: 'The Unbroken' },
  { key: 'games_lost', label: 'The Defeated' },
  { key: 'tutorial_missions_complete', label: 'The Volunteer' },
  { key: 'black_ops_missions_complete', label: 'The Commander' },
  { key: 'strategy_subscriber_count', label: 'Chief Strategist' },
  { key: 'giant_slayer', label: 'Giant Slayer' },
  { key: 'war_effort_mission_win', label: 'The Conqueror' },
  { key: 'deploy_empty_zone_count', label: 'Master of Inefficiency' },
  { key: 'envoy_all_accords', label: 'The Envoy' },
  { key: 'prepared_all_bonuses', label: 'The Prepared' },
  { key: 'commander_all_avatars', label: 'Commander in Chief' },
  { key: 'founder', label: 'The Founder' },
  { key: 'ghost_operative_views', label: 'The Visionary' },
  { key: 'explore_unknown_level_20', label: 'The Explorer' },
  { key: 'explore_unknown_level_60', label: 'The Fearless' },
  { key: 'explore_unknown_level_100', label: 'The Worldwalker' }
];

// Faction colors for orb backgrounds
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

// Get faction orb background color/gradient (matching Strategy.js and WareEffort.js implementation)
const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};

const STARTER_STRATEGY_NAME_MARKER = '(Starter)';

function findStarterStrategy(strategies) {
  if (!Array.isArray(strategies)) {
    return null;
  }
  return (
    strategies.find(
      (strategy) =>
        strategy?.id != null &&
        typeof strategy.name === 'string' &&
        strategy.name.includes(STARTER_STRATEGY_NAME_MARKER)
    ) ?? null
  );
}

/** When selection is missing or no longer in the list, pick a subscribed starter deck. */
function resolveEmptyStrategySelection(currentSelection, strategies) {
  const selectedId = currentSelection ? currentSelection.toString() : '';
  const stillListed = selectedId && strategies.some((strategy) => strategy.id.toString() === selectedId);
  if (stillListed) {
    return null;
  }
  const starter = findStarterStrategy(strategies);
  if (!starter) {
    return null;
  }
  return starter.id.toString();
}

function BattleOverview({ user, onLogout }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const { showNotification, NotificationComponent } = useNotifications();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  useMusic(MAIN_MENU_MUSIC_PATH);
  const { current: pendingToast, dismissCurrent: dismissPendingToast } = usePendingAchievementToasts();
  
  const [strategies, setStrategies] = useState([]);
  // Separate strategy selections for PvP, PvE (Train), and Dispatch Asset
  const [selectedPvpStrategy, setSelectedPvpStrategy] = useState(localStorage.getItem('selectedPvpStrategy') || localStorage.getItem('selectedStrategy') || '');
  const [selectedPveStrategy, setSelectedPveStrategy] = useState(localStorage.getItem('selectedPveStrategy') || localStorage.getItem('selectedStrategy') || '');
  const [selectedAutobattlerStrategy, setSelectedAutobattlerStrategy] = useState(localStorage.getItem('selectedAutobattlerStrategy') || localStorage.getItem('selectedStrategy') || '');
  const [showQueuePopup, setShowQueuePopup] = useState(false);
  const [queueTime, setQueueTime] = useState(0);
  const [queueInterval, setQueueInterval] = useState(null);
  const [queueStatus, setQueueStatus] = useState('');
  const [ws, setWs] = useState(null);
  const [showSimulateAchievementPanel, setShowSimulateAchievementPanel] = useState(false);
  const [simulatedAchievement, setSimulatedAchievement] = useState(null);
  const navigate = useNavigate();
  const [hasActiveGame, setHasActiveGame] = useState(false);
  const [activeGameInfo, setActiveGameInfo] = useState(null); // { isBotGame, opponentName, reconnectGameKind }
  const preserveWsRef = useRef(false);
  const [strategyValidations, setStrategyValidations] = useState({}); // { strategyId: { valid: boolean, insufficientOrders: [], fetchError?: boolean } }
  /** In-game modal for ownership / validation messages (replaces window.alert). */
  const [ownershipMessageDialog, setOwnershipMessageDialog] = useState(null); // { title: string, body?: string } | null
  const [factionTranslations, setFactionTranslations] = useState({}); // Map: english_name -> translated_name
  
  // Check if current user is player1 (for restricted features like developer mode)
  const userId = typeof user === 'string' ? user : (user?.id || user?.username);
  const isPlayer1 = userId === 'player1' || userId === 1 || user?.username === 'player1';
  const canEnterRestrictedModes = isPlayer1;

  const dismissModeSticker = (messageKey) => {
    markBattleOverviewModeEntered(messageKey, markSeenLocally).catch((err) => {
      console.error('Failed to dismiss battle overview mode sticker:', err);
    });
  };

  const showModeSticker = (messageKey) =>
    Boolean(user) && tutorialStatusLoaded && shouldShowTutorial(messageKey);
  
  // Avatar state (from localStorage as fallback - actual avatars come from game state in BattleUI)
  const [selectedGeneral, setSelectedGeneral] = useState(localStorage.getItem('selectedGeneral') || '');
  const [selectedAdvisor, setSelectedAdvisor] = useState(localStorage.getItem('selectedAdvisor') || '');

  // Tutorial state - persisted via user_tutorial_seen (messageKey: battle_overview_attack)
  const [showTutorialBattleOverview, setShowTutorialBattleOverview] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(1);
  const { shouldShowTutorial, isLoaded: tutorialStatusLoaded, markSeenLocally } = useTutorialStatus(user);
  useMenuTutorialReplay('battle_overview_attack', {
    enabled: Boolean(user) && tutorialStatusLoaded,
    onReplay: () => {
      setTutorialStep(1);
      setShowTutorialBattleOverview(true);
    },
  });

  useEffect(() => {
    if (user && tutorialStatusLoaded && shouldShowTutorial('battle_overview_attack')) {
      const timer = setTimeout(() => {
        setShowTutorialBattleOverview(true);
        setTutorialStep(1);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [user, tutorialStatusLoaded, shouldShowTutorial]);

  useEffect(() => {
    if (showTutorialBattleOverview && tutorialStep === 2) {
      const v = getEffectiveSoundOutputVolume(1);
      if (v === null) return;
      const audio = new Audio('/sounds/alarm.mp3');
      audio.volume = v;
      audio.play().catch(() => {});
      return () => {
        audio.pause();
        audio.currentTime = 0;
      };
    }
  }, [showTutorialBattleOverview, tutorialStep]);

  // Preload critical UI images for BattleOverview
  useEffect(() => {
    const criticalImages = [
      '/images/battleoverview.jpg',
      '/images/pvp.jpg',
      '/images/pve.jpg',
      '/images/rejoin.jpg',
      '/images/blackops.jpg',
      '/images/wareffort.jpg',
      '/images/sp.jpg',
      '/images/280.jpg',
    ];

    criticalImages.forEach((src) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    });
  }, []);

  // Helper function to calculate total order count from a strategy
  const getStrategyOrderCount = (strategy) => {
    if (!strategy.orders || !Array.isArray(strategy.orders)) return 0;
    return strategy.orders.reduce((total, order) => total + (order.count || 0), 0);
  };

  // Helper function to check if strategy has valid order count (50-100)
  const isValidOrderCount = (strategy) => {
    const orderCount = getStrategyOrderCount(strategy);
    return orderCount >= 50 && orderCount <= 100;
  };

  // Helper function to ensure token is valid before API calls
  const ensureValidToken = async () => {
    let token = localStorage.getItem('token');
    const refreshTokenValue = localStorage.getItem('refreshToken');
    
    if (token && refreshTokenValue) {
      // Check if token is expired or about to expire
      if (!isTokenValid(token) || needsTokenRefresh(token)) {
        try {
          console.log('🔄 Token expired or about to expire, refreshing...');
          token = await refreshToken(refreshTokenValue);
          localStorage.setItem('token', token);
          console.log('✅ Token refreshed successfully');
        } catch (error) {
          console.error('❌ Token refresh failed:', error);
          throw new Error('Token refresh failed. Please refresh the page.');
        }
      }
    } else if (!token) {
      throw new Error('No authentication token. Please log in again.');
    }
    
    return token;
  };

  const startCampaignTutorialFromOverview = async () => {
    try {
      const token = await ensureValidToken();
      const activeRunResponse = await fetch(`${API_URL}/explore-unknown/active-run`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!activeRunResponse.ok) {
        const errorBody = await activeRunResponse.text();
        throw new Error(`Failed to verify Explore Unknown run state (${activeRunResponse.status}): ${errorBody}`);
      }

      const activeRun = await activeRunResponse.json();
      if (activeRun && activeRun.runId != null) {
        setOwnershipMessageDialog({
          title: t('battleOverview.tutorial.activeExploreUnknownRunTitle', 'Explore Unknown Run Active'),
          body: t(
            'battleOverview.tutorial.activeExploreUnknownRunBody',
            'You currently have an active Explore Unknown run. Finish or abandon it before starting the campaign tutorial mission.'
          )
        });
        return;
      }

      const confirmed = window.confirm(
        t(
          'battleOverview.tutorial.confirmCampaignStart',
          'Start Campaign Tutorial Mission 1 now?'
        )
      );
      if (!confirmed) {
        return;
      }

      navigate('/campaign-battle/chapter_1_mission_1');
    } catch (error) {
      console.error('❌ BattleOverview: Failed to start tutorial campaign mission safely:', error);
      setOwnershipMessageDialog({
        title: t('battleOverview.tutorial.failedToVerifyRunTitle', 'Unable to start tutorial mission'),
        body: error?.message || t('battleOverview.tutorial.failedToVerifyRunBody', 'Could not verify your Explore Unknown run state. Please try again.')
      });
    }
  };

  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        // ✅ FIX: Refresh token if needed before making API calls
        const token = await ensureValidToken();
        
        const res = await fetch(`${API_URL}/my-strategies`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (data.error) {
          console.error("Bot battle error:", data.error);
          showNotification(`${t('battleOverview.errorStartingBot')} ${data.error}`, 'error', 6000);
          return;
        }

        if (Array.isArray(data)) {
          // Filter strategies to only include those with 50-100 orders
          const validStrategies = data.filter(strategy => isValidOrderCount(strategy));
          setStrategies(validStrategies);
          // Validate all strategies in parallel (faster load; avoids long window where validations are empty)
          const validationEntries = await Promise.all(
            validStrategies
              .filter((strategy) => strategy.id != null)
              .map(async (strategy) => {
                const idKey = String(strategy.id);
                try {
                  const currentToken = await ensureValidToken();
                  const validationRes = await fetch(`${API_URL}/strategy/${strategy.id}/validate`, {
                    headers: {
                      Authorization: `Bearer ${currentToken}`,
                    },
                  });
                  const rawBody = await validationRes.json().catch(() => ({}));
                  const normalized = normalizeStrategyValidationResponse(validationRes, rawBody);
                  return [idKey, normalized];
                } catch (err) {
                  console.error(`Failed to validate strategy ${strategy.id}:`, err);
                  return [
                    idKey,
                    {
                      valid: false,
                      insufficientOrders: [],
                      fetchError: true,
                      errorMessage: err instanceof Error ? err.message : String(err),
                    },
                  ];
                }
              })
          );
          setStrategyValidations(Object.fromEntries(validationEntries));
        } else {
          console.error('Unexpected response format:', data);
          setStrategies([]);
        }
      } catch (err) {
        console.error('Failed to fetch strategies:', err);
        if (err.message && (err.message.includes('Token refresh failed') || err.message.includes('No authentication token'))) {
          showNotification(err.message, 'error', 8000);
        }
        setStrategies([]);
        setStrategyValidations({});
      }
    };

    const checkActiveGame = async () => {
      try {
        // ✅ FIX: Refresh token if needed before making API calls
        const token = await ensureValidToken();
        
        const res = await fetch(`${API_URL}/check-active-game`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const text = await res.text(); // get raw response
        const data = JSON.parse(text);
        const hasActive = data.hasActiveGame === true;
        setHasActiveGame(hasActive);
        
        // Set active game info from the endpoint response
        if (hasActive) {
          const reconnectGameKind =
            data.reconnectGameKind != null
              ? data.reconnectGameKind
              : data.isBotGame === true
                ? 'unknown'
                : 'pvp';
          setActiveGameInfo({
            isBotGame: data.isBotGame === true,
            opponentName: data.opponentName || null,
            reconnectGameKind,
          });
        } else {
          setActiveGameInfo(null);
        }
      } catch (err) {
        console.error('❌ Fetch failed:', err);
        // Don't show alert for checkActiveGame failures - it's not critical
        setHasActiveGame(false);
        setActiveGameInfo(null);
      }
    };

    fetchStrategies();
    checkActiveGame();

    
  }, []);

  // Preselect a (Starter) deck for Engage the Enemy / Train when nothing valid is selected
  useEffect(() => {
    if (strategies.length === 0) {
      return;
    }

    setSelectedPvpStrategy((prev) => {
      const resolved = resolveEmptyStrategySelection(prev, strategies);
      if (resolved == null) {
        return prev;
      }
      localStorage.setItem('selectedPvpStrategy', resolved);
      return resolved;
    });

    setSelectedPveStrategy((prev) => {
      const resolved = resolveEmptyStrategySelection(prev, strategies);
      if (resolved == null) {
        return prev;
      }
      localStorage.setItem('selectedPveStrategy', resolved);
      return resolved;
    });
  }, [strategies]);

  // Fetch faction translations
  useEffect(() => {
    const fetchFactionTranslations = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const factions = await response.json();
          const translationMap = {};
          factions.forEach(faction => {
            translationMap[faction.english_name] = faction.name;
          });
          setFactionTranslations(translationMap);
        }
      } catch (err) {
        console.error('Failed to fetch faction translations:', err);
        // Fallback: use English names
        setFactionTranslations({});
      }
    };
    
    fetchFactionTranslations();
  }, [i18nInstance.language]);

  useEffect(() => {
    return () => {
      // Keep WS open if we're handing it off to BattleUI
      if (ws && !preserveWsRef.current) ws.close();
    };
  }, [ws]);

  useEffect(() => {
    if (showQueuePopup) {
      setQueueTime(0);
      const interval = setInterval(() => setQueueTime(t => t + 1), 1000);
      setQueueInterval(interval);
      return () => clearInterval(interval);
    } else if (queueInterval) {
      clearInterval(queueInterval);
    }
  }, [showQueuePopup]);

  const handleBattleClick = () => {
    if (ws) return;
    if (!selectedPvpStrategy) {
      setOwnershipMessageDialog({
        title: t('battleOverview.cannotStartBattleTitle'),
        body: t('battleOverview.selectStrategy'),
      });
      return;
    }

    // Validate strategy before starting battle
    const validation = strategyValidations[selectedPvpStrategy];
    if (!validation) {
      setOwnershipMessageDialog({
        title: t('battleOverview.ownershipStillCheckingTitle'),
        body: t('battleOverview.ownershipStillCheckingBody'),
      });
      return;
    }
    if (validation.valid !== true) {
      if (validation.fetchError) {
        setOwnershipMessageDialog({
          title: t('battleOverview.ownershipCheckFailedTitle'),
          body: validation.errorMessage || t('battleOverview.ownershipCheckFailedBody'),
        });
        return;
      }
      const insufficientOrders = validation.insufficientOrders || [];
      if (insufficientOrders.length > 0) {
        const orderList = insufficientOrders.map(o => `${o.name} (need ${o.required}, have ${o.owned})`).join(', ');
        setOwnershipMessageDialog({
          title: t('battleOverview.cannotStartBattleTitle'),
          body: `${t('battleOverview.cannotStartBattleInsufficientIntro')}\n\n${orderList}`,
        });
      } else {
        setOwnershipMessageDialog({
          title: t('battleOverview.cannotStartBattleTitle'),
          body: t('battleOverview.cannotStartBattleUnowned'),
        });
      }
      return;
    }

    setShowQueuePopup(true);
    setQueueStatus(t('battleOverview.searchingOpponent'));
    dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.engageEnemy);
    // Update Discord presence to "In Queue"
    updateDiscordPresence('In Queue', null, true);

    const socket = new window.WebSocket(WS_URL);
    setWs(socket);

    socket.onopen = () => {
      // Get current language from i18next (normalize to lowercase format)
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      const token = localStorage.getItem('token');
      socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 [BATTLE OVERVIEW] Received WebSocket message:', data.type, data);

      if (data.type === 'auth-success') {
        socket.send(
          JSON.stringify({
            type: 'join-queue',
            strategyId: selectedPvpStrategy,
          })
        );
      }

      if (data.type === 'queue-waiting') {
        setQueueStatus(t('battleOverview.searchingOpponent'));
      }

if (data.type === 'match-found') {
  console.log("📨 Received match-found:", data);
  if (!data.gameId) {
    console.error("❌ Missing gameId in match-found");
    return;
  }
  console.log("🧭 Attempting to navigate to /battle");
  preserveWsRef.current = true;
  navigate(`/battle/${data.gameId}`, {
    state: {
      strategyId: selectedPvpStrategy,
      token: localStorage.getItem('token'),
      userId: localStorage.getItem('userId'),
      selectedGeneral: selectedGeneral,
      selectedAdvisor: selectedAdvisor,
      // ✅ FIX: Pass gameState and yourTurn from match-found to prevent unnecessary reconnect
      // This ensures both players can join the game immediately without seeing "reconnecting..."
      gameState: data.gameState,
      yourTurn: data.yourTurn,
    },
  });
  // We no longer pass ws via state to avoid DataCloneError
  // Close local socket; BattleUI will reconnect using token
  try { socket.close(); } catch {}
  console.log("✅ Navigation triggered");
}

      if (data.type === 'queue-left') {
        setQueueStatus('');
        setShowQueuePopup(false);
        socket.close();
        // Clear Discord presence when leaving queue
        clearDiscordPresence();
      }

      if (data.type === 'error') {
        // ✅ FIX: Show error message as in-game notification instead of browser alert
        console.log('❌ [BATTLE OVERVIEW] Error received:', data.message);
        const errorMsg = data.message || t('battleOverview.error');
        showNotification(errorMsg, 'error', 6000); // Show for 6 seconds
        setQueueStatus(t('battleOverview.error') + ' ' + data.message);
        setShowQueuePopup(false);
        socket.close();
        // Clear Discord presence on error
        clearDiscordPresence();
      }
      
      if (data.type === 'queue-joined') {
        console.log('✅ [BATTLE OVERVIEW] Queue joined successfully');
      }
    };

    socket.onerror = (err) => {
      setQueueStatus(t('battleOverview.websocketError'));
      setShowQueuePopup(false);
      socket.close();
      // Clear Discord presence on error
      clearDiscordPresence();
    };

    socket.onclose = () => {
      setWs(null);
    };
  };

  const handleCancelQueue = () => {
    setShowQueuePopup(false);
    setQueueStatus('');
    if (ws) {
      ws.send(JSON.stringify({ type: 'leave-queue' }));
      ws.close();
      setQueueTime(0);
    }
    // Clear Discord presence when canceling queue
    clearDiscordPresence();
  };

const handleBotBattleClick = async (isDeveloperMode = false) => {
  if (ws) return;
  // Developer mode uses PvE strategy, regular bot battles use PvE strategy
  const strategyToUse = selectedPveStrategy;
  if (!strategyToUse) {
    setOwnershipMessageDialog({
      title: t('battleOverview.cannotStartBattleTitle'),
      body: t('battleOverview.selectStrategy'),
    });
    return;
  }

  // Validate strategy before starting battle
  const validation = strategyValidations[strategyToUse];
  if (!validation) {
    setOwnershipMessageDialog({
      title: t('battleOverview.ownershipStillCheckingTitle'),
      body: t('battleOverview.ownershipStillCheckingBody'),
    });
    return;
  }
  if (validation.valid !== true) {
    if (validation.fetchError) {
      setOwnershipMessageDialog({
        title: t('battleOverview.ownershipCheckFailedTitle'),
        body: validation.errorMessage || t('battleOverview.ownershipCheckFailedBody'),
      });
      return;
    }
    const insufficientOrders = validation.insufficientOrders || [];
    if (insufficientOrders.length > 0) {
      const orderList = insufficientOrders.map(o => `${o.name} (need ${o.required}, have ${o.owned})`).join(', ');
      setOwnershipMessageDialog({
        title: t('battleOverview.cannotStartBattleTitle'),
        body: `${t('battleOverview.cannotStartBattleInsufficientIntro')}\n\n${orderList}`,
      });
    } else {
      setOwnershipMessageDialog({
        title: t('battleOverview.cannotStartBattleTitle'),
        body: t('battleOverview.cannotStartBattleUnowned'),
      });
    }
    return;
  }

  setShowQueuePopup(true);
  setQueueStatus(t('battleOverview.startingBotBattle'));
  dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.train);
  // Update Discord presence to "In Queue" for bot battle
  updateDiscordPresence('In Queue', null, true);

  // ✅ CRITICAL: Refresh token if needed before starting bot game
  let token = localStorage.getItem('token');
  const refreshTokenValue = localStorage.getItem('refreshToken');
  
  if (token && refreshTokenValue) {
    // Check if token is expired or about to expire
    if (!isTokenValid(token) || needsTokenRefresh(token)) {
      try {
        console.log('🔄 Token expired or about to expire, refreshing...');
        setQueueStatus(t('battleOverview.refreshingToken') || 'Refreshing token...');
        token = await refreshToken(refreshTokenValue);
        localStorage.setItem('token', token);
        console.log('✅ Token refreshed successfully');
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        setQueueStatus(t('battleOverview.error') + ' ' + (t('battleOverview.tokenRefreshFailed') || 'Token refresh failed. Please refresh the page.'));
        setShowQueuePopup(false);
        return;
      }
    }
  } else if (!token) {
    console.error('❌ No token found in localStorage');
    setQueueStatus(t('battleOverview.error') + ' ' + (t('battleOverview.noToken') || 'No authentication token. Please log in again.'));
    setShowQueuePopup(false);
    return;
  }

  const socket = new window.WebSocket(WS_URL);
  setWs(socket);

  socket.onopen = () => {
    // Get current language from i18next (normalize to lowercase format)
    const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
    const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
    // Use centralized auth message with API key
    socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);

    if (data.type === 'auth-success') {
      socket.send(
        JSON.stringify({
          type: 'start-bot-game',
          strategyId: strategyToUse,
          developerMode: isDeveloperMode
        })
      );
    }

    if (data.type === 'match-found') {
      console.log("📨 Received match-found:", data);
      setQueueStatus(t('battleOverview.botMatchStarted'));
      setShowQueuePopup(false);
      try { socket.close(); } catch {}

      const navigationState = {
        strategyId: strategyToUse,
        userId: localStorage.getItem('userId'),
        token: localStorage.getItem('token'),
        selectedGeneral: selectedGeneral,
        selectedAdvisor: selectedAdvisor,
        isBotGame: true,
        gameState: data.gameState, // ✅ FIX: Pass gameState to avoid reconnect
        yourTurn: data.yourTurn
      };
      
      if (isDeveloperMode) {
        navigationState.developerMode = true;
      }
      
      navigate(`/battle/${data.gameId}`, {
        state: navigationState
      });
    }

    if (data.type === 'error') {
      // ✅ FIX: Show error message as in-game notification instead of browser alert
      const errorMsg = data.message || t('battleOverview.error');
      showNotification(errorMsg, 'error', 6000); // Show for 6 seconds
      setQueueStatus(t('battleOverview.error') + ' ' + data.message);
      setShowQueuePopup(false);
      socket.close();
      // Clear Discord presence on error
      clearDiscordPresence();
    }
  };

  socket.onerror = (err) => {
    setQueueStatus(t('battleOverview.websocketError'));
    setShowQueuePopup(false);
    socket.close();
    // Clear Discord presence on error
    clearDiscordPresence();
  };

  socket.onclose = () => {
    setWs(null);
  };
};

const handleAutobattlerClick = async () => {
  if (ws) return;
  const strategyToUse = selectedAutobattlerStrategy;
  if (!strategyToUse) {
    setOwnershipMessageDialog({
      title: t('battleOverview.cannotStartBattleTitle'),
      body: t('battleOverview.selectStrategy'),
    });
    return;
  }

  // Validate strategy before starting battle
  const validation = strategyValidations[strategyToUse];
  if (!validation) {
    setOwnershipMessageDialog({
      title: t('battleOverview.ownershipStillCheckingTitle'),
      body: t('battleOverview.ownershipStillCheckingBody'),
    });
    return;
  }
  if (validation.valid !== true) {
    if (validation.fetchError) {
      setOwnershipMessageDialog({
        title: t('battleOverview.ownershipCheckFailedTitle'),
        body: validation.errorMessage || t('battleOverview.ownershipCheckFailedBody'),
      });
      return;
    }
    const insufficientOrders = validation.insufficientOrders || [];
    if (insufficientOrders.length > 0) {
      const orderList = insufficientOrders.map(o => `${o.name} (need ${o.required}, have ${o.owned})`).join(', ');
      setOwnershipMessageDialog({
        title: t('battleOverview.dispatchAsset'),
        body: `${t('battleOverview.cannotStartDispatchAsset')}\n\n${orderList}`,
      });
    } else {
      setOwnershipMessageDialog({
        title: t('battleOverview.dispatchAsset'),
        body: t('battleOverview.cannotStartDispatchAssetNoOrders'),
      });
    }
    return;
  }

  setShowQueuePopup(true);
  setQueueStatus(t('battleOverview.startingDispatchAsset') || 'Starting Dispatch Asset...');
  // Update Discord presence to "In Queue" for Dispatch Asset
  updateDiscordPresence('In Queue', null, true);

  // ✅ CRITICAL: Refresh token if needed before starting Dispatch Asset game
  let token = localStorage.getItem('token');
  const refreshTokenValue = localStorage.getItem('refreshToken');
  
  if (token && refreshTokenValue) {
    // Check if token is expired or about to expire
    if (!isTokenValid(token) || needsTokenRefresh(token)) {
      try {
        console.log('🔄 Token expired or about to expire, refreshing...');
        setQueueStatus(t('battleOverview.refreshingToken') || 'Refreshing token...');
        token = await refreshToken(refreshTokenValue);
        localStorage.setItem('token', token);
        console.log('✅ Token refreshed successfully');
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        setQueueStatus(t('battleOverview.error') + ' ' + (t('battleOverview.tokenRefreshFailed') || 'Token refresh failed. Please refresh the page.'));
        setShowQueuePopup(false);
        return;
      }
    }
  } else if (!token) {
    console.error('❌ No token found in localStorage');
    setQueueStatus(t('battleOverview.error') + ' ' + (t('battleOverview.noToken') || 'No authentication token. Please log in again.'));
    setShowQueuePopup(false);
    return;
  }

  const socket = new window.WebSocket(WS_URL);
  setWs(socket);

  socket.onopen = () => {
    // Get current language from i18next (normalize to lowercase format)
    const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
    const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
    // Use centralized auth message with API key
    socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);

    if (data.type === 'auth-success') {
      socket.send(
        JSON.stringify({
          type: 'autobattler-start',
          strategyId: parseInt(strategyToUse)
        })
      );
    }

    if (data.type === 'match-found') {
      console.log("📨 Received match-found for Dispatch Asset:", data);
      setQueueStatus(t('battleOverview.dispatchAssetStarted') || 'Dispatch Asset started');
      setShowQueuePopup(false);
      try { socket.close(); } catch {}
      setWs(null);

      const navigationState = {
        strategyId: strategyToUse,
        userId: localStorage.getItem('userId'),
        token: localStorage.getItem('token'),
        selectedGeneral: selectedGeneral,
        selectedAdvisor: selectedAdvisor,
        isBotGame: false, // Not a bot game, it's Dispatch Asset
        gameState: data.gameState,
        yourTurn: false // Player is spectator, never their turn
      };
      
      navigate(`/battle/${data.gameId}`, {
        state: navigationState
      });
    } else       if (data.type === 'error') {
        // ✅ FIX: Show error message as in-game notification instead of browser alert
        console.error('❌ Dispatch Asset error:', data.message);
        const errorMsg = data.message || t('battleOverview.failedToStartDispatchAsset') || 'Failed to start Dispatch Asset game';
        showNotification(errorMsg, 'error', 6000); // Show for 6 seconds
        setQueueStatus(t('battleOverview.error') + ' ' + (data.message || t('battleOverview.failedToStartDispatchAsset')));
        setShowQueuePopup(false);
        try { socket.close(); } catch {}
        setWs(null);
        clearDiscordPresence();
      }
  };

  socket.onerror = (error) => {
    console.error('WebSocket error:', error);
    alert('Connection error. Please try again.');
    setShowQueuePopup(false);
    try { socket.close(); } catch {}
    setWs(null);
    clearDiscordPresence();
  };

  socket.onclose = () => {
    setWs(null);
  };
};

const handleReconnectClick = async () => {
  if (ws) return;
  if (hasActiveGame) {
    dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.rejoin);
  }

  // ✅ FIX: Refresh token if needed before reconnecting
  let token = localStorage.getItem('token');
  const refreshTokenValue = localStorage.getItem('refreshToken');
  
  if (token && refreshTokenValue) {
    if (!isTokenValid(token) || needsTokenRefresh(token)) {
      try {
        console.log('🔄 Token expired or about to expire, refreshing...');
        token = await refreshToken(refreshTokenValue);
        localStorage.setItem('token', token);
        console.log('✅ Token refreshed successfully');
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        alert(t('battleOverview.error') + ' ' + (t('battleOverview.tokenRefreshFailed') || 'Token refresh failed. Please refresh the page.'));
        return;
      }
    }
  } else if (!token) {
    alert(t('battleOverview.error') + ' ' + (t('battleOverview.noToken') || 'No authentication token. Please log in again.'));
    return;
  }

  const socket = new window.WebSocket(WS_URL);
  setWs(socket);

  socket.onopen = () => {
    // Step 1: Authenticate first with API key
    const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
    const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
    socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
console.log("📡 WebSocket message received:", data);

    // Step 2: Wait for auth confirmation
    if (data.type === 'auth-success') {
      socket.send(JSON.stringify({
        type: 'reconnect',
        token: localStorage.getItem('token'),
      }));
    }

    // Step 3: Handle reconnect success
    if (data.type === 'reconnect-success') {
      socket.close();
  navigate(`/battle/${data.gameId}`, {
    state: {
      gameId: data.gameId,
      gameState: data.gameState,
      yourTurn: data.yourTurn,
      selectedGeneral: selectedGeneral,
      selectedAdvisor: selectedAdvisor,
    },
  });
    }

    // Step 4: Handle errors
    if (data.type === 'error') {
      alert(t('battleOverview.reconnectFailed') + ' ' + data.message);
      socket.close();
    }
  };

  socket.onerror = () => {
    alert(t('battleOverview.websocketErrorReconnect'));
    socket.close();
  };

  socket.onclose = () => {
    setWs(null);
  };
};



return (
    <div className="battle-overview-viewport-wrapper">
      {pendingToast && (
        <AchievementUnlockedToast
          titleId={pendingToast.titleId}
          titleName={pendingToast.titleName}
          color={pendingToast.color}
          achievementKey={pendingToast.achievementKey}
          onClose={dismissPendingToast}
        />
      )}
      {simulatedAchievement && (
        <AchievementUnlockedToast
          titleId={simulatedAchievement.titleId}
          titleName={simulatedAchievement.titleName}
          color={simulatedAchievement.color}
          achievementKey={simulatedAchievement.achievementKey}
          onClose={() => setSimulatedAchievement(null)}
        />
      )}
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="battle-overview-fullscreen-background"
        style={{ backgroundImage: 'url(/images/battleoverview.jpg)' }}
      />

      {showTutorialBattleOverview && tutorialStep === 2 && (
        <div className="battle-overview-tutorial-flicker" aria-hidden="true" />
      )}
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`battle-overview-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="battle-overview-container">
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
          </div>

      <button onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        navigate('/welcome');
      }} className="back-button">
        {t('common.back')}
      </button>

      {/* Dev Reward Demo Buttons - Only visible for player1 */}
      {isPlayer1 && (
        <>
          <button 
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              // Create fake rewards: 2 orders + billy bucks
              const fakeRewards = [
                {
                  type: 'order',
                  data: {
                    id: 1,
                    name: 'Test Order 1',
                    rarity: 'rare',
                    type: 'unit',
                    cost: 5,
                    attack_rating: 3,
                    health: 4,
                    faction_id: 1,
                    faction_name: 'Remnants of Humanity',
                    description: 'Dev test order 1'
                  }
                },
                {
                  type: 'billy_bucks',
                  amount: 100,
                  data: { amount: 100 }
                },
                {
                  type: 'order',
                  data: {
                    id: 2,
                    name: 'Test Order 2',
                    rarity: 'epic',
                    type: 'unit',
                    cost: 7,
                    attack_rating: 5,
                    health: 6,
                    faction_id: 2,
                    faction_name: 'Exolot Dominion',
                    description: 'Dev test order 2'
                  }
                }
              ];
              
              navigate('/after-battle', {
                state: {
                  result: 'win',
                  rewards: fakeRewards,
                  isFriendlyMatch: false,
                  isTournamentMatch: false
                }
              });
            }}
            style={{
              position: 'absolute',
              top: '60px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#ff6b6b',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev Reward Demo
          </button>
          <button 
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              // Create fake Black Ops rewards: 2 orders + intel
              const fakeBlackOpsRewards = [
                {
                  id: 1,
                  name: 'Test Order 1',
                  rarity: 'rare',
                  type: 'unit',
                  cost: 5,
                  attack_rating: 3,
                  health: 4,
                  faction_id: 1,
                  faction_name: 'Remnants of Humanity',
                  description: 'Dev test order 1'
                },
                {
                  id: 2,
                  name: 'Test Order 2',
                  rarity: 'epic',
                  type: 'unit',
                  cost: 7,
                  attack_rating: 5,
                  health: 6,
                  faction_id: 2,
                  faction_name: 'Exolot Dominion',
                  description: 'Dev test order 2'
                }
              ];
              
              navigate('/blackops', {
                state: {
                  devRewards: fakeBlackOpsRewards,
                  devIntel: 5
                }
              });
            }}
            style={{
              position: 'absolute',
              top: '110px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#4a90e2',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev Black Ops Rewards
          </button>
          <button 
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              // Create fake Dispatch Asset rewards: All factions × All rarities
              const baseFactions = [
                { id: 1, name: 'Remnants of Humanity' },
                { id: 2, name: 'Exolot Dominion' },
                { id: 3, name: 'League of Shadows' },
                { id: 4, name: 'Ra\'s Children' },
                { id: 5, name: 'Asrah' }
              ];
              
              const combinedFactions = [
                { id: 6, name: 'The Conspiracy' },
                { id: 7, name: 'The Corrupted Core' },
                { id: 8, name: 'The Covenant' },
                { id: 9, name: 'The Divine Syndicate' },
                { id: 10, name: 'The Forsaken Pact' },
                { id: 11, name: 'The Hollow' },
                { id: 12, name: 'The Iron Alliance' },
                { id: 13, name: 'The Last Crusade' },
                { id: 14, name: 'The Shadow Consortium' },
                { id: 15, name: 'The Silent Accord' }
              ];
              
              const allFactions = [...baseFactions, ...combinedFactions];
              const rarities = ['common', 'rare', 'epic', 'legendary'];
              
              const fakeDispatchRewards = [];
              let orderId = 1;
              
              // Generate orders for all factions and rarities
              allFactions.forEach((faction, factionIndex) => {
                rarities.forEach((rarity, rarityIndex) => {
                  // Calculate stats based on rarity
                  const rarityMultipliers = {
                    common: { cost: 3, attack: 2, health: 2 },
                    rare: { cost: 5, attack: 3, health: 3 },
                    epic: { cost: 7, attack: 5, health: 4 },
                    legendary: { cost: 9, attack: 7, health: 6 }
                  };
                  
                  const multiplier = rarityMultipliers[rarity];
                  
                  fakeDispatchRewards.push({
                    type: 'order',
                    data: {
                      id: orderId++,
                      name: `${faction.name} ${rarity.charAt(0).toUpperCase() + rarity.slice(1)} Unit`,
                      rarity: rarity,
                      type: 'unit',
                      cost: multiplier.cost,
                      attack_rating: multiplier.attack,
                      health: multiplier.health,
                      faction_id: faction.id,
                      faction_name: faction.name,
                      description: `Dev test ${rarity} order from ${faction.name}`
                    }
                  });
                });
              });
              
              // Add some resource rewards
              fakeDispatchRewards.push({
                type: 'resource',
                resourceType: 'stone',
                amount: 1000,
                data: { resourceType: 'stone', amount: 1000 }
              });
              
              fakeDispatchRewards.push({
                type: 'resource',
                resourceType: 'billy_bucks',
                amount: 500,
                data: { resourceType: 'billy_bucks', amount: 500 }
              });
              
              navigate('/dispatch-asset', {
                state: {
                  devRewards: fakeDispatchRewards
                }
              });
            }}
            style={{
              position: 'absolute',
              top: '160px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#9b59b6',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev Dispatch Rewards
          </button>
          <button 
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              // Create fake starting rewards: 50 orders with various rarities
              const fakeStartingRewards = [];
              const rarities = ['common', 'rare', 'epic', 'legendary'];
              const factions = [
                { id: 1, name: 'Remnants of Humanity' },
                { id: 2, name: 'Exolot Dominion' },
                { id: 3, name: 'League of Shadows' },
                { id: 4, name: 'Ra\'s Children' },
                { id: 5, name: 'Asrah' },
                { id: 6, name: 'The Sentients' }
              ];
              
              for (let i = 1; i <= 50; i++) {
                const rarity = rarities[Math.floor(Math.random() * rarities.length)];
                const faction = factions[Math.floor(Math.random() * factions.length)];
                fakeStartingRewards.push({
                  id: i,
                  name: `Starting Order ${i}`,
                  rarity: rarity,
                  type: 'unit',
                  cost: Math.floor(Math.random() * 10) + 1,
                  attack_rating: Math.floor(Math.random() * 10) + 1,
                  health: Math.floor(Math.random() * 10) + 1,
                  faction_id: faction.id,
                  faction_name: faction.name,
                  description: `Dev test starting order ${i}`
                });
              }
              
              navigate('/welcome', {
                state: {
                  devStartingRewards: fakeStartingRewards
                }
              });
            }}
            style={{
              position: 'absolute',
              top: '210px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#e67e22',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev Starting Rewards
          </button>
          <button
            onClick={async () => {
              playSoundEffect('/sounds/click.mp3');
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/dev-simulate-tutorial-reward`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  }
                });
                const data = await res.json();
                if (!res.ok) {
                  alert(data.error || 'Failed to simulate tutorial reward');
                  return;
                }
                navigate('/after-battle', {
                  state: {
                    gameId: data.gameId,
                    result: 'win',
                    isCampaign: true,
                    missionId: 'chapter_1_mission_1',
                    isFriendlyMatch: false,
                    isTournamentMatch: false
                  }
                });
              } catch (err) {
                console.error('Dev tutorial reward error:', err);
                alert('Failed to simulate tutorial reward');
              }
            }}
            style={{
              position: 'absolute',
              top: '260px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#27ae60',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev Tutorial Rewards
          </button>
          <button
            onClick={async () => {
              playSoundEffect('/sounds/click.mp3');
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/dev-simulate-war-effort-rewards`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  },
                  body: JSON.stringify({ outcome: 'win' })
                });
                const data = await res.json();
                if (!res.ok) {
                  alert(data.error || 'Failed to simulate War Effort rewards');
                  return;
                }
                navigate('/after-battle', {
                  state: {
                    gameId: data.gameId,
                    result: data.outcome || 'win',
                    isWarEffort: true,
                    gameState: { warEffortMissionId: true, warEffortBattleType: 'enemy_hq' },
                    isFriendlyMatch: false,
                    isTournamentMatch: false
                  }
                });
              } catch (err) {
                console.error('Dev War Effort reward error:', err);
                alert('Failed to simulate War Effort rewards');
              }
            }}
            style={{
              position: 'absolute',
              top: '310px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#8e44ad',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev War Effort Rewards
          </button>
          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              const mockPvpLadder = {
                rankIndex: 0,
                rankKey: 'second_lieutenant',
                stars: 1,
                maxStars: 2,
                promoted: false,
                reachedGeneral: false,
                starsDelta: 1,
                mmrBefore: 1000,
                mmrAfter: 1000,
                mmrDelta: 0,
                inLeague: false
              };
              navigate('/after-battle', {
                state: {
                  result: 'win',
                  devPvpRankMock: true,
                  isEngageEnemyQueue: true,
                  pvpLadder: mockPvpLadder,
                  rewards: [],
                  rewardsGranted: true
                }
              });
            }}
            style={{
              position: 'absolute',
              top: '410px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#16a085',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev PvP rank (win)
          </button>
          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              const mockPvpLadder = {
                rankIndex: 1,
                rankKey: 'first_lieutenant',
                stars: 0,
                maxStars: 3,
                promoted: false,
                reachedGeneral: false,
                starsDelta: 0,
                mmrBefore: 1000,
                mmrAfter: 1000,
                mmrDelta: 0,
                inLeague: false
              };
              navigate('/after-battle', {
                state: {
                  result: 'loss',
                  devPvpRankMock: true,
                  isEngageEnemyQueue: true,
                  pvpLadder: mockPvpLadder,
                  rewards: [],
                  rewardsGranted: true
                }
              });
            }}
            style={{
              position: 'absolute',
              top: '460px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#117a65',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev PvP rank (loss)
          </button>
          <button
            onClick={async () => {
              playSoundEffect('/sounds/click.mp3');
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/dev-war-effort-rewards-loser`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  }
                });
                const data = await res.json();
                if (!res.ok) {
                  alert(data.error || 'Failed to set up War Effort loser screen');
                  return;
                }
                navigate('/after-battle', {
                  state: {
                    gameId: data.gameId,
                    result: 'loss',
                    isWarEffort: true,
                    gameState: { warEffortMissionId: data.missionId, warEffortBattleType: 'enemy_hq' },
                    fromWarEffortMissionOver: true,
                    missionIdForDismiss: data.missionId
                  }
                });
              } catch (err) {
                console.error('Dev War Effort loser screen error:', err);
                alert('Failed to set up War Effort loser screen');
              }
            }}
            style={{
              position: 'absolute',
              top: '360px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#c0392b',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev War Effort Rewards Loser
          </button>
          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              const mockEuRunRewardsSummary = {
                statistics: {
                  missionReached: 40,
                  endReason: 'loss',
                  opponentFactionName: null,
                  bossIntroTranslationKey: 'exploreUnknownPage.bossIntro.mirth',
                  otherPlayersReachedCount: 3,
                },
                resources: {
                  lavender: 120,
                  stone: 45,
                  biomass: 30,
                },
                billyBucks: 275,
                buildings: [
                  { building_id: 'research_center' },
                  { building_id: 'barracks' },
                ],
                orders: [
                  {
                    id: 1,
                    name: 'Dev Legendary Unit',
                    rarity: 'legendary',
                    type: 'unit',
                    cost: 9,
                    attack_rating: 7,
                    health: 6,
                    faction_id: 4,
                    faction_name: "Ra's Children",
                    description: 'Mock legendary for EU loss screen',
                  },
                  {
                    id: 2,
                    name: 'Dev Epic Unit',
                    rarity: 'epic',
                    type: 'unit',
                    cost: 7,
                    attack_rating: 5,
                    health: 5,
                    faction_id: 2,
                    faction_name: 'Exolot Dominion',
                    description: 'Mock epic for EU loss screen',
                  },
                  {
                    id: 3,
                    name: 'Dev Common Unit',
                    rarity: 'common',
                    type: 'unit',
                    cost: 3,
                    attack_rating: 2,
                    health: 2,
                    faction_id: 1,
                    faction_name: 'Remnants of Humanity',
                    description: 'Mock common for EU loss screen',
                  },
                ],
              };
              navigate('/after-battle', {
                state: {
                  result: 'loss',
                  exploreUnknownRun: true,
                  devExploreUnknownRunLossMock: true,
                  exploreUnknownRunRewardsSummary: mockEuRunRewardsSummary,
                  gameState: {
                    exploreUnknownMissionIndex: 5,
                    exploreUnknownRunId: 1,
                  },
                  isFriendlyMatch: false,
                  isTournamentMatch: false,
                },
              });
            }}
            style={{
              position: 'absolute',
              top: '510px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#2c3e50',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold',
            }}
          >
            🧪 Dev EU loss rewards
          </button>
          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              const mockEuRunHomecomingSummary = {
                statistics: {
                  missionReached: 74,
                  endReason: 'complete',
                  opponentFactionName: null,
                  bossIntroTranslationKey: null,
                  otherPlayersReachedCount: 2,
                },
                resources: {
                  lavender: 200,
                  erray: 80,
                },
                billyBucks: 500,
                buildings: [{ building_id: 'portal' }],
                orders: [
                  {
                    id: 1,
                    name: 'Dev Legendary Unit',
                    rarity: 'legendary',
                    type: 'unit',
                    cost: 9,
                    attack_rating: 7,
                    health: 6,
                    faction_id: 4,
                    faction_name: "Ra's Children",
                    description: 'Mock legendary for EU homecoming screen',
                  },
                ],
              };
              navigate('/after-battle', {
                state: {
                  result: 'win',
                  exploreUnknownRun: true,
                  exploreUnknownRunHomecoming: true,
                  devExploreUnknownRunHomecomingMock: true,
                  exploreUnknownRunRewardsSummary: mockEuRunHomecomingSummary,
                  gameState: {
                    exploreUnknownMissionIndex: 74,
                    exploreUnknownRunId: 1,
                  },
                  isFriendlyMatch: false,
                  isTournamentMatch: false,
                },
              });
            }}
            style={{
              position: 'absolute',
              top: '560px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#7c3aed',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold',
            }}
          >
            🧪 Dev EU homecoming
          </button>
          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              navigate('/after-battle', {
                state: buildExploreUnknownDevMockWinAfterBattleNavigateState(),
              });
            }}
            style={{
              position: 'absolute',
              top: '610px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#16a34a',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold',
            }}
          >
            🧪 Dev EU win rewards
          </button>
          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              navigate('/after-battle', {
                state: {
                  result: 'win',
                  isCampaign: true,
                  missionId: 'chapter_1_mission_2',
                  isFriendlyMatch: false,
                  isTournamentMatch: false,
                  devM2Choices: [
                    { key: 'aggressive', value: 'Aggressive' },
                    { key: 'greedy', value: 'Greedy' },
                    { key: 'patient', value: 'Patient' },
                    { key: 'controlling', value: 'Controlling' },
                    { key: 'creative', value: 'Creative' }
                  ]
                }
              });
            }}
            style={{
              position: 'absolute',
              top: '520px',
              right: '20px',
              padding: '8px 16px',
              backgroundColor: '#9b59b6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            🧪 Dev Tutorial 2 Rewards
          </button>
        </>
      )}

      {/* Main Game Mode Buttons Grid */}
      <div className="game-mode-grid">
        {/* Battle Button - PVP */}
        <div className="game-mode-button-wrapper">
          <div style={{ position: 'absolute', top: '10px', left: '10px', right: '10px', zIndex: 10 }}>
            <CustomDropdown
              options={strategies.map(s => ({
                value: s.id.toString(),
                label: s.name,
                faction_name: s.faction_name,
                hasWarning:
                  strategyValidations[s.id] != null && strategyValidations[s.id].valid !== true,
              }))}
              value={selectedPvpStrategy ? selectedPvpStrategy.toString() : ''}
              onChange={(strategyId) => {
                playSoundEffect('/sounds/click.mp3');
                const value = strategyId ? strategyId.toString() : '';
                setSelectedPvpStrategy(value);
                localStorage.setItem('selectedPvpStrategy', value);
              }}
              placeholder="-- Select Strategy --"
              renderOption={(option) => {
                const strategy = strategies.find(s => s.id.toString() === option.value);
                const factionName = strategy?.faction_name || option.faction_name;
                const validation = strategyValidations[option.value];
                const showWarningIcon = validation != null && validation.valid !== true;
                
                if (!factionName) {
                  return <span>{option.label} {showWarningIcon ? '⚠️' : ''}</span>;
                }
                const translatedFactionName = factionTranslations[factionName] || factionName;
                
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      className="faction-orb"
                      style={{ background: getFactionOrbBackground(factionName) }}
                      title={translatedFactionName}
                    />
                    <span>{option.label} {showWarningIcon ? '⚠️' : ''}</span>
                  </div>
                );
              }}
            />
          </div>
          <button
            className="game-mode-button battle-button"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              handleBattleClick();
            }}
            disabled={!selectedPvpStrategy || strategyValidations[selectedPvpStrategy]?.valid !== true}
            style={{
              backgroundImage: 'url(/images/pvp.jpg)',
            }}
          >
            <GameModeSticker
              labelKey="battleOverview.tagEngageEnemy"
              variant="recommended"
              visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.engageEnemy)}
            />
            <span className="game-mode-text">{t('battleOverview.engageEnemy')}</span>
            <span className="game-mode-description-scroll" aria-hidden="true">
              <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionEngageEnemy')}</span>
            </span>
          </button>
        </div>

        {/* Train Button - PVE */}
        <div className="game-mode-button-wrapper">
          <div style={{ position: 'absolute', top: '10px', left: '10px', right: '10px', zIndex: 10 }}>
            <CustomDropdown
              options={strategies.map(s => ({
                value: s.id.toString(),
                label: s.name,
                faction_name: s.faction_name,
                hasWarning:
                  strategyValidations[s.id] != null && strategyValidations[s.id].valid !== true,
              }))}
              value={selectedPveStrategy ? selectedPveStrategy.toString() : ''}
              onChange={(strategyId) => {
                playSoundEffect('/sounds/click.mp3');
                const value = strategyId ? strategyId.toString() : '';
                setSelectedPveStrategy(value);
                localStorage.setItem('selectedPveStrategy', value);
              }}
              placeholder={t('battleOverview.selectStrategyDropdown')}
              renderOption={(option) => {
                const strategy = strategies.find(s => s.id.toString() === option.value);
                const factionName = strategy?.faction_name || option.faction_name;
                const validation = strategyValidations[option.value];
                const showWarningIcon = validation != null && validation.valid !== true;
                
                if (!factionName) {
                  return <span>{option.label} {showWarningIcon ? '⚠️' : ''}</span>;
                }
                const translatedFactionName = factionTranslations[factionName] || factionName;
                
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      className="faction-orb"
                      style={{ background: getFactionOrbBackground(factionName) }}
                      title={translatedFactionName}
                    />
                    <span>{option.label} {showWarningIcon ? '⚠️' : ''}</span>
                  </div>
                );
              }}
            />
          </div>
          <button
            className="game-mode-button train-button"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              handleBotBattleClick(false);
            }}
            disabled={!selectedPveStrategy || strategyValidations[selectedPveStrategy]?.valid !== true}
            style={{
              backgroundImage: 'url(/images/pve.jpg)',
            }}
          >
            <GameModeSticker
              labelKey="battleOverview.tagTrain"
              visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.train)}
            />
            <span className="game-mode-text">{t('battleOverview.train')}</span>
            <span className="game-mode-description-scroll" aria-hidden="true">
              <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionTrain')}</span>
            </span>
          </button>
        </div>

        {/* Reconnect Button - Always visible */}
        <button
          className="game-mode-button reconnect-button"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            handleReconnectClick();
          }}
          disabled={!hasActiveGame}
          style={{
            backgroundImage: 'url(/images/rejoin.jpg)',
          }}
        >
          <GameModeSticker
            labelKey="battleOverview.tagRejoin"
            visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.rejoin)}
          />
          <span className="game-mode-text">
            {hasActiveGame
              ? (activeGameInfo && activeGameInfo.isBotGame
                  ? (() => {
                      const rk = activeGameInfo.reconnectGameKind;
                      if (rk === 'war_effort') return t('battleOverview.rejoinWarEffort');
                      if (rk === 'explore_unknown') return t('battleOverview.rejoinExploreUnknown');
                      if (rk === 'campaign') return t('battleOverview.rejoinAnthology');
                      if (rk === 'training') return t('battleOverview.rejoinTraining');
                      return t('battleOverview.rejoinBattle');
                    })()
                  : activeGameInfo && activeGameInfo.opponentName
                    ? `${t('battleOverview.rejoinOpponent')} ${activeGameInfo.opponentName}`
                    : t('battleOverview.rejoinOpponent'))
                : t('battleOverview.nothingToRejoin')}
          </span>
          <span className="game-mode-description-scroll" aria-hidden="true">
            <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionRejoin')}</span>
          </span>
        </button>

        {/* Black Ops Button */}
        <button
          className="game-mode-button blackops-button"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.blackOps);
            navigate('/blackops');
          }}
          style={{
            backgroundImage: 'url(/images/blackops.jpg)',
          }}
        >
          <GameModeSticker
            labelKey="battleOverview.tagBlackOps"
            visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.blackOps)}
          />
          <span className="game-mode-text">{t('battleOverview.blackOps')}</span>
          <span className="game-mode-description-scroll" aria-hidden="true">
            <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionBlackOps')}</span>
          </span>
        </button>

        {/* War Effort Button */}
        <button
          className={`game-mode-button wareffort-button${canEnterRestrictedModes ? '' : ' game-mode-button--access-restricted'}`}
          onClick={() => {
            if (!canEnterRestrictedModes) return;
            playSoundEffect('/sounds/click.mp3');
            dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.warEffort);
            navigate('/wareffort');
          }}
          disabled={!canEnterRestrictedModes}
          title={canEnterRestrictedModes ? undefined : t('battleOverview.tagWarEffort')}
          style={{
            backgroundImage: 'url(/images/wareffort.jpg)',
          }}
        >
          <GameModeSticker
            labelKey="battleOverview.tagWarEffort"
            variant="restricted"
            visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.warEffort)}
          />
          <span className="game-mode-text">{t('battleOverview.warEffort')}</span>
          <span className="game-mode-description-scroll" aria-hidden="true">
            <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionWarEffort')}</span>
          </span>
        </button>

        {/* Campaign Button */}
        <button
          className="game-mode-button campaign-button"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.anthology);
            navigate('/campaign');
          }}
          style={{
            backgroundImage: 'url(/images/sp.jpg)',
          }}
        >
          <GameModeSticker
            labelKey="battleOverview.tagAnthology"
            visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.anthology)}
          />
          <span className="game-mode-text">{t('battleOverview.anthology')}</span>
          <span className="game-mode-description-scroll" aria-hidden="true">
            <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionAnthology')}</span>
          </span>
        </button>

        {/* Proving Grounds Button */}
        <button
          className={`game-mode-button provinggrounds-button${canEnterRestrictedModes ? '' : ' game-mode-button--access-restricted'}`}
          onClick={() => {
            if (!canEnterRestrictedModes) return;
            playSoundEffect('/sounds/click.mp3');
            dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.provingGrounds);
            navigate('/provinggrounds');
          }}
          disabled={!canEnterRestrictedModes}
          title={canEnterRestrictedModes ? undefined : t('battleOverview.tagProvingGrounds')}
          style={{
            '--tournament-bg': 'url(/images/tournament.jpg)',
          }}
        >
          <GameModeSticker
            labelKey="battleOverview.tagProvingGrounds"
            variant="restricted"
            visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.provingGrounds)}
          />
          <span className="game-mode-text">{t('battleOverview.provingGrounds')}</span>
          <span className="game-mode-description-scroll" aria-hidden="true">
            <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionProvingGrounds')}</span>
          </span>
        </button>

        {/* Dispatch Asset Button */}
        <button
          className="game-mode-button autobattler-button"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.dispatchAsset);
            navigate('/dispatch-asset');
          }}
          style={{
            backgroundImage: 'url(/images/dispatch_asset_bg.jpg)',
          }}
        >
          <GameModeSticker
            labelKey="battleOverview.tagDispatchAsset"
            visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.dispatchAsset)}
          />
          <span className="game-mode-text">{t('battleOverview.dispatchAsset')}</span>
          <span className="game-mode-description-scroll" aria-hidden="true">
            <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionDispatchAsset')}</span>
          </span>
        </button>

        {/* Explore the unknown */}
        <button
          type="button"
          className="game-mode-button explore-unknown-button"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            dismissModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.exploreUnknown);
            navigate('/explore-unknown');
          }}
          style={{
            backgroundImage: 'url(/images/blackops_erray.jpg)',
          }}
        >
          <GameModeSticker
            labelKey="battleOverview.tagExploreUnknown"
            variant="recommended"
            visible={showModeSticker(BATTLE_OVERVIEW_MODE_STICKER_KEYS.exploreUnknown)}
          />
          <span className="game-mode-text">{t('battleOverview.exploreUnknown')}</span>
          <span className="game-mode-description-scroll" aria-hidden="true">
            <span className="game-mode-description-scroll-inner">{t('battleOverview.descriptionExploreUnknown')}</span>
          </span>
        </button>
      </div>

      {/* Lower Right Corner Controls */}
      <div className="lower-right-controls">
        {/* Developer Mode Button (PLAYER 1 ONLY) */}
        {process.env.NODE_ENV === 'development' && isPlayer1 && (
          <>
            <button
              className="developer-mode-button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                handleBotBattleClick(true);
              }}
              disabled={!selectedPveStrategy || strategyValidations[selectedPveStrategy]?.valid !== true}
            >
              {t('battleOverview.developerMode')}
            </button>
            <button
              className="developer-mode-button"
              style={{ marginTop: '10px' }}
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                window.location.href = '/dev-autobattler';
              }}
            >
              Dev Autobattler
            </button>
            <button
              className="developer-mode-button"
              style={{ marginTop: '10px' }}
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setShowSimulateAchievementPanel(true);
              }}
            >
              Simulate achievement
            </button>
          </>
        )}
      </div>

      {showQueuePopup && (
        <div className="queue-popup">
          <div className="queue-popup-content">
            <h3>{queueStatus}</h3>
            <p>{t('battleOverview.timeInQueue')} {queueTime} {t('battleOverview.seconds')}</p>
            <button onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              handleCancelQueue();
            }}>{t('common.cancel')}</button>
          </div>
        </div>
      )}

      {ownershipMessageDialog && (
        <div className="queue-popup" role="alertdialog" aria-modal="true" aria-labelledby="battle-overview-ownership-dialog-title">
          <div className="queue-popup-content">
            <h3 id="battle-overview-ownership-dialog-title">{ownershipMessageDialog.title}</h3>
            {ownershipMessageDialog.body ? (
              <p style={{ whiteSpace: 'pre-line', marginTop: '12px', textAlign: 'left', maxHeight: '50vh', overflowY: 'auto' }}>
                {ownershipMessageDialog.body}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setOwnershipMessageDialog(null);
              }}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {showSimulateAchievementPanel && (
        <div className="simulate-achievement-overlay" onClick={() => { playSoundEffect('/sounds/click.mp3'); setShowSimulateAchievementPanel(false); }}>
          <div className="simulate-achievement-panel" onClick={e => e.stopPropagation()}>
            <div className="simulate-achievement-panel-header">
              <h3>Simulate achievement</h3>
              <button type="button" className="simulate-achievement-close" onClick={() => { playSoundEffect('/sounds/click.mp3'); setShowSimulateAchievementPanel(false); }} aria-label="Close">×</button>
            </div>
            <div className="simulate-achievement-list">
              {DEV_ACHIEVEMENT_KEYS.map(({ key: achievementKey, label }) => (
                <button
                  key={achievementKey}
                  type="button"
                  className="simulate-achievement-item"
                  onClick={async () => {
                    playSoundEffect('/sounds/click.mp3');
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_URL}/profile/dev-simulate-achievement`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ achievementKey })
                      });
                      const data = await res.json();
                      if (!res.ok) {
                        alert(data.error || 'Failed to simulate achievement');
                        return;
                      }
                      if (data.achievementUnlocked) {
                        setSimulatedAchievement(data.achievementUnlocked);
                        setShowSimulateAchievementPanel(false);
                      }
                    } catch (err) {
                      console.error('Dev simulate achievement error:', err);
                      alert('Failed to simulate achievement');
                    }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* In-game notification component */}
      {NotificationComponent}

      <TutorialReplayButton
        user={user}
        onReplay={() => {
          setTutorialStep(1);
          setShowTutorialBattleOverview(true);
        }}
      />

      {showTutorialBattleOverview && createPortal(
        tutorialStep === 1 ? (
          <TutorialMessageBox
            image="/images/tutorial_explain_2.png"
            text={t('battleOverview.tutorial.introText')}
            goldText={t('battleOverview.tutorial.introTextGold')}
            onContinue={() => setTutorialStep(2)}
            voiceKeys={BATTLE_OVERVIEW_INTRO_VOICE_KEYS}
          />
        ) : (
          <TutorialMessageBox
            image="/images/tutorial_angry_point_2.png"
            text={t('battleOverview.tutorial.attackText')}
            choices={[
              { key: 'runAway', value: 'run_away' },
              { key: 'faceTheMusic', value: 'face_the_music' }
            ]}
            choiceLabelPrefix="battleOverview.tutorial"
            onSelect={(value) => {
              setShowTutorialBattleOverview(false);
              if (value === 'run_away') {
                navigate('/welcome');
              } else if (value === 'face_the_music') {
                startCampaignTutorialFromOverview();
              }
            }}
            onContinue={() => {}}
            voiceKey="battleOverview.tutorial.attackText"
          />
        ),
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default BattleOverview;
